import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { useHubAccountName } from "@/hooks/useHubResearch";
import { isFeatureEnabled } from "@/config/client";
import { AccountContactsTab } from "./tabs/AccountContactsTab";
import { DrugPipelineTab } from "./tabs/DrugPipelineTab";
import { ClinicalTrialsTab } from "./tabs/ClinicalTrialsTab";
import { NIHGrantsTab } from "./tabs/NIHGrantsTab";
import { SECFilingsTab } from "./tabs/SECFilingsTab";
import { PatentsTab } from "./tabs/PatentsTab";
import { AccountPipelineOpsTab } from "./tabs/AccountPipelineOpsTab";
import { AccountNewsTab } from "./tabs/AccountNewsTab";
import { AccountEngagementTab } from "./tabs/AccountEngagementTab";
import { GoldSheetTab } from "./tabs/GoldSheetTab";
import { PipelinePrepTab } from "./tabs/PipelinePrepTab";
import { AccountPIsTab } from "./tabs/AccountPIsTab";
import { AccountLabsTab } from "./tabs/AccountLabsTab";
import { AccountChildrenTab } from "./tabs/AccountChildrenTab";
import { AccountSubsidiariesTab } from "./tabs/AccountSubsidiariesTab";
import { AccountActivityTab } from "./tabs/AccountActivityTab";

interface AccountDetailTabsProps {
  company: Record<string, any>;
}

const ACADEMIC_CATEGORIES = ["academic_institution"];

export function AccountDetailTabs({ company }: AccountDetailTabsProps) {
  // Look up the Hub account name via life_science_id for better cross-DB matching
  const { data: hubAccount } = useHubAccountName(company.life_science_id || null);
  const hubAccountName = hubAccount?.name || null;

  const isAcademic = ACADEMIC_CATEGORIES.includes(company.primary_category);

  // Check if this company has subsidiaries (LinkedIn URL-based parent/child)
  const { data: subsidiaryCount } = useQuery({
    queryKey: ["company-subsidiary-count", company.id],
    queryFn: async () => {
      const { count } = await supabase
        .from("companies")
        .select("id", { count: "exact", head: true })
        .eq("parent_company_id", company.id);
      return count ?? 0;
    },
  });

  const hasSubsidiaries = (subsidiaryCount ?? 0) > 0;

  const tabs = useMemo(() => {
    const baseTabs = [
      { id: "gold-sheet", label: "Gold Sheet" },
    ];

    if (isAcademic) {
      baseTabs.push(
        { id: "pis", label: "PIs" },
        { id: "labs", label: "Labs" },
        { id: "children", label: "Children" },
      );
    }

    baseTabs.push(
      { id: "contacts", label: "Contacts" },
    );

    if (isFeatureEnabled('pipeline')) {
      baseTabs.push(
        { id: "pipeline-ops", label: "Pipeline Opps" },
        { id: "pipeline-prep", label: "Pipeline Prep" },
      );
    }

    baseTabs.push(
      { id: "clinical-trials", label: "Clinical Trials" },
      { id: "drug-pipeline", label: "Drug Pipeline" },
      { id: "nih-grants", label: "NIH Grants" },
      { id: "sec-filings", label: "SEC Filings" },
      { id: "patents", label: "Patents" },
      { id: "news", label: "News" },
      { id: "engagement", label: "Engagement" },
      { id: "activity", label: "Activity" },
    );

    if (hasSubsidiaries) {
      baseTabs.push({ id: "subsidiaries", label: `Subsidiaries (${subsidiaryCount})` });
    }

    return baseTabs;
  }, [isAcademic, hasSubsidiaries, subsidiaryCount]);

  const [activeTab, setActiveTab] = useState(tabs[0]?.id || "contacts");

  return (
    <div className="mt-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full h-12 bg-muted rounded-t-lg rounded-b-none border-b border-border justify-start gap-0 p-0 overflow-x-auto">
          {tabs.map(tab => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-background data-[state=active]:shadow-none px-6 h-full text-sm font-medium whitespace-nowrap"
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="gold-sheet" className="mt-0">
          <Card className="rounded-t-none border-t-0">
            <CardContent className="p-0">
              <GoldSheetTab company={company} />
            </CardContent>
          </Card>
        </TabsContent>

        {isAcademic && (
          <>
            <TabsContent value="pis" className="mt-0">
              <Card className="rounded-t-none border-t-0">
                <CardContent className="p-0">
                  <AccountPIsTab accountId={company.id} />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="labs" className="mt-0">
              <Card className="rounded-t-none border-t-0">
                <CardContent className="p-0">
                  <AccountLabsTab accountId={company.id} />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="children" className="mt-0">
              <Card className="rounded-t-none border-t-0">
                <CardContent className="p-0">
                  <AccountChildrenTab accountId={company.id} />
                </CardContent>
              </Card>
            </TabsContent>
          </>
        )}

        <TabsContent value="contacts" className="mt-0">
          <Card className="rounded-t-none border-t-0">
            <CardContent className="p-0">
              <AccountContactsTab accountId={company.id} companyName={company.company_name} companyDomain={company.domain} />
            </CardContent>
          </Card>
        </TabsContent>

        {isFeatureEnabled('pipeline') && (
          <>
            <TabsContent value="pipeline-ops" className="mt-0">
              <Card className="rounded-t-none border-t-0">
                <CardContent className="p-0">
                  <AccountPipelineOpsTab accountId={company.id} companyName={company.company_name} companyDomain={company.domain} />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="pipeline-prep" className="mt-0">
              <Card className="rounded-t-none border-t-0">
                <CardContent className="p-0">
                  <PipelinePrepTab company={company} />
                </CardContent>
              </Card>
            </TabsContent>
          </>
        )}

        <TabsContent value="clinical-trials" className="mt-0">
          <Card className="rounded-t-none border-t-0">
            <CardContent className="p-0">
              <ClinicalTrialsTab accountId={company.id} companyName={company.company_name} hubAccountName={hubAccountName} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="drug-pipeline" className="mt-0">
          <Card className="rounded-t-none border-t-0">
            <CardContent className="p-0">
              <DrugPipelineTab accountId={company.id} companyName={company.company_name} company={company} hubAccountName={hubAccountName} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="nih-grants" className="mt-0">
          <Card className="rounded-t-none border-t-0">
            <CardContent className="p-0">
              <NIHGrantsTab accountId={company.id} companyName={company.company_name} hubAccountName={hubAccountName} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sec-filings" className="mt-0">
          <Card className="rounded-t-none border-t-0">
            <CardContent className="p-0">
              <SECFilingsTab accountId={company.id} companyName={company.company_name} hubAccountName={hubAccountName} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="patents" className="mt-0">
          <Card className="rounded-t-none border-t-0">
            <CardContent className="p-0">
              <PatentsTab accountId={company.id} companyName={company.company_name} hubAccountName={hubAccountName} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="news" className="mt-0">
          <Card className="rounded-t-none border-t-0">
            <CardContent className="p-0">
              <AccountNewsTab accountId={company.id} companyName={company.company_name} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="engagement" className="mt-0">
          <Card className="rounded-t-none border-t-0">
            <CardContent className="p-0">
              <AccountEngagementTab accountId={company.id} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activity" className="mt-0">
          <Card className="rounded-t-none border-t-0">
            <CardContent className="p-0">
              <AccountActivityTab accountId={company.id} />
            </CardContent>
          </Card>
        </TabsContent>

        {hasSubsidiaries && (
          <TabsContent value="subsidiaries" className="mt-0">
            <Card className="rounded-t-none border-t-0">
              <CardContent className="p-0">
                <AccountSubsidiariesTab accountId={company.id} />
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
